import classNames from 'classnames'
import React from 'react'

import type { UI2QuantityFieldProps } from 'shared-libs/components/ui2/fields/ui2-quantity-field/ui2-quantity-field-props'

import { UI2FieldControl } from 'browser/components/ui2/fields/field-ui/field-control/field-control'
import { UI2NoValueAvailable } from 'browser/components/ui2/fields/field-ui/no-value-available/no-value-available'
import { UI2UnitPicker } from 'browser/components/ui2/fields/field-ui/unit-picker/ui2-unit-picker'

import { UI2FieldShell, IUI2FieldShellProps } from '../field-ui/field-shell/field-shell'
import './_ui2-quantity-field.scss'

type UnitOption = { label: string; value: string }

interface IUI2QuantityWebExtras {
  className?: string
  testID?: string
  accessibilityLabel?: string
  placeholder?: string
  // Overrides the shared-libs onChange signature to accept both plain numbers
  // and { value, unit } objects (when unitOptions are configured).
  onChange?: (value: any) => void
}

/**
 * @uiComponent
 */
export interface IUI2QuantityFieldProps extends Omit<UI2QuantityFieldProps, 'onChange'>, IUI2QuantityWebExtras {}

interface IUI2QuantityFieldState {
  internalValue: string
  internalUnit: string | undefined
}

function isQuantityObject(val: any): val is { unit: string; value: number } {
  return val !== null && val !== undefined && typeof val === 'object' && 'value' in val
}

function preventStepperFocus(e: React.MouseEvent<HTMLButtonElement>): void {
  e.preventDefault()
}

export class UI2QuantityField extends React.Component<IUI2QuantityFieldProps, IUI2QuantityFieldState> {
  constructor(props: IUI2QuantityFieldProps) {
    super(props)
    this.state = {
      internalValue: UI2QuantityField.toInternalString(props.value),
      internalUnit: UI2QuantityField.toInternalUnit(props),
    }
  }

  private static toInternalString(value: any): string {
    if (isQuantityObject(value)) return value.value != null ? String(value.value) : ''
    return value != null ? String(value) : ''
  }

  private static toInternalUnit(props: IUI2QuantityFieldProps): string | undefined {
    const { value, unitOptions } = props
    if (isQuantityObject(value) && value.unit) return value.unit
    return unitOptions?.[0]?.value
  }

  public UNSAFE_componentWillReceiveProps(nextProps: IUI2QuantityFieldProps) {
    const nextState: Partial<IUI2QuantityFieldState> = {}
    if (nextProps.value !== this.props.value) {
      nextState.internalValue = UI2QuantityField.toInternalString(nextProps.value)
      if (isQuantityObject(nextProps.value) && nextProps.value.unit) {
        nextState.internalUnit = nextProps.value.unit
      }
    }
    if (Object.keys(nextState).length > 0) {
      this.setState(nextState as IUI2QuantityFieldState)
    }
  }

  private getCurrentUnit(): string | undefined {
    return this.state.internalUnit
  }

  private getCurrentNumericValue(): number | undefined {
    const { internalValue } = this.state
    if (internalValue === '' || internalValue === '-') return undefined
    const n = parseInt(internalValue, 10)
    return isNaN(n) ? undefined : n
  }

  private emitChange(numericValue: number | undefined, unit?: string): void {
    const { onChange, value, unitOptions } = this.props
    if (!onChange) return
    const hasUnits = !!unitOptions?.length
    if (isQuantityObject(value) || hasUnits) {
      const effectiveUnit = unit ?? this.getCurrentUnit()
      onChange(numericValue != null ? { value: numericValue, unit: effectiveUnit } : undefined)
    } else {
      onChange(numericValue)
    }
  }

  private getUnitDisplay(unitValue: string | undefined): string | undefined {
    const { unitOptions } = this.props
    if (unitValue == null) return unitOptions?.[0]?.label
    const match = unitOptions?.find((o) => o.value === unitValue)
    return match?.label ?? unitValue
  }

  public render() {
    const { className } = this.props
    return (
      <UI2FieldShell
        {...(this.props as unknown as IUI2FieldShellProps)}
        className={classNames('ui2-quantityField', className)}
        renderReadOnly={this.renderReadOnly}
        // Body is a UI2FieldControl, which slots Cancel beside the input.
        bodyIsFieldControl={true}
      >
        <UI2FieldControl rightAddon={this.renderRightAddon()}>
          <input
            type='number'
            value={this.state.internalValue}
            onChange={this.handleValueChange}
            placeholder={this.props.placeholder ?? (this.props.label ? `Enter ${this.props.label}` : undefined)}
            disabled={this.props.isDisabled}
            data-testid={this.props.testID}
            aria-label={this.props.accessibilityLabel}
          />
        </UI2FieldControl>
      </UI2FieldShell>
    )
  }

  private renderReadOnly = (value: any, { editNode }: { editNode?: React.ReactNode } = {}): React.ReactNode => {
    let content: React.ReactNode
    if (value == null) {
      content = <UI2NoValueAvailable />
    } else if (isQuantityObject(value)) {
      const unitDisplay = this.getUnitDisplay(value.unit)
      const display = value.value != null
        ? (unitDisplay ? `${value.value} ${unitDisplay}` : String(value.value))
        : ''
      content = <span>{display}</span>
    } else {
      const effectiveUnitLabel = this.getEffectiveUnitLabel()
      content = <span>{effectiveUnitLabel ? `${value} ${effectiveUnitLabel}` : String(value)}</span>
    }
    return (
      <>
        <div className='ui2-fieldShell-readOnly-content'>{content}</div>
        {editNode}
      </>
    )
  }

  private getEffectiveUnitLabel(): string | undefined {
    const { unitOptions, unitLabel, value } = this.props
    if (unitOptions?.length) return undefined
    if (unitLabel === null) return undefined
    if (isQuantityObject(value)) return undefined
    return unitLabel ?? 'units'
  }

  private renderRightAddon(): React.ReactNode {
    const { showStepper, unitOptions, isDisabled, isReadOnly } = this.props
    const hasSteppers = !!showStepper
    const optionCount = unitOptions?.length ?? 0
    const effectiveUnitLabel = this.getEffectiveUnitLabel()

    if (!hasSteppers && optionCount === 0 && !effectiveUnitLabel) return undefined

    return (
      <>
        {hasSteppers && (
          <>
            <button
              className='ui2-fieldControl-interactive ui2-quantityField-stepBtn'
              type='button'
              disabled={isDisabled}
              tabIndex={-1}
              onMouseDown={preventStepperFocus}
              onClick={this.handleDecrement}
            >
              −
            </button>
            <button
              className='ui2-fieldControl-interactive ui2-quantityField-stepBtn'
              type='button'
              disabled={isDisabled}
              tabIndex={-1}
              onMouseDown={preventStepperFocus}
              onClick={this.handleIncrement}
            >
              +
            </button>
          </>
        )}
        {optionCount === 1 && (
          <span className='ui2-fieldControl-staticBadge'>{unitOptions![0].label}</span>
        )}
        {optionCount > 1 && (
          <UI2UnitPicker
            options={unitOptions!}
            value={this.getCurrentUnit()}
            onChange={this.handleUnitChange}
            isDisabled={isDisabled || isReadOnly}
          />
        )}
        {effectiveUnitLabel && (
          <span className='ui2-fieldControl-staticBadge'>{effectiveUnitLabel}</span>
        )}
      </>
    )
  }

  private handleUnitChange = (nextUnit: string): void => {
    this.setState({ internalUnit: nextUnit }, () => {
      this.emitChange(this.getCurrentNumericValue(), nextUnit)
    })
  }

  private handleValueChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const raw = e.target.value
    const sanitized = raw.replace(/[^0-9-]/g, '')
    if (sanitized !== '' && sanitized !== '-' && !/^-?[0-9]*$/.test(sanitized)) return
    this.setState({ internalValue: sanitized }, () => {
      this.emitChange(this.getCurrentNumericValue(), this.getCurrentUnit())
    })
  }

  private handleIncrement = (): void => {
    const { stepSize = 1, max } = this.props
    const current = this.getCurrentNumericValue() ?? 0
    const next = current + stepSize
    if (max != null && next > max) return
    this.setState({ internalValue: String(next) }, () => {
      this.emitChange(next, this.getCurrentUnit())
    })
  }

  private handleDecrement = (): void => {
    const { stepSize = 1, min } = this.props
    const current = this.getCurrentNumericValue() ?? 0
    const next = current - stepSize
    if (min != null && next < min) return
    this.setState({ internalValue: String(next) }, () => {
      this.emitChange(next, this.getCurrentUnit())
    })
  }
}
